/**
 * web/src/components/workflow/WorkflowCard.tsx
 * Workflow row card for the WorkflowList page (UI-03).
 *
 * Source: planning-docs/design_handoff_pixel_dashboard/design_files/pages.jsx
 *         lines 119-157 (WorkflowCard prototype);
 *         design_files/styles.css lines 720-820 (.wf-card + variants).
 *
 * 4-column grid: 4px type-tinted rail | 32px status-icon col | main col | side meta col.
 *
 * Field mapping (CONTEXT.md / 05-PATTERNS.md lines 1151-1158):
 *  prototype `w.name` → server `workflow.displayName`
 *  prototype `w.currentStep` (1-based) → `currentStepIndex + 1` (server is 0-based)
 *  prototype `w.totalSteps` → `getStepCount(w.type)` (10 for sponsor, 4 for test)
 *  prototype `w.contractValue` → workflow.context.trigger_and_extract.contractValue
 *  prototype `w.error` → server's most recent failed step errorMessage (passed in
 *      from WorkflowList; the card itself does not fetch step rows).
 *
 * Click → workflowPath(w): /workflows/:id, except speaker onboarding which
 * lands on the speaker hub (/speakers/:eventId/:itemId — the journey view IS
 * its workflow view). Failed workflows render an admin-only Retry button that
 * calls onRetry(stepIndex) — caller wires the mutation.
 *
 * Threat T-05-07-02 / T-05-07-06: displayName + errorMessage are rendered as
 * React text nodes (auto-escaped). errorMessage is also truncated with a
 * `title` attribute so reviewers can hover to see the full string.
 */
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Dot } from '../shared/Dot';
import { Progress } from '../shared/Progress';
import { StatusBadge, deriveWorkflowStatusKey, type StatusKey } from '../shared/StatusBadge';
import type { DotStatus } from '../shared/Dot';
import { Button } from '../shared/Button';
import { RoleGuard } from '../layout/RoleGuard';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { getStepCount } from '../../lib/workflow-step-defs';
import { workflowPath } from '../../lib/workflowPath';
import type { Workflow } from '../../api/types';

/**
 * Reads the contract value from the workflow context. Prefers the LLM-
 * extracted value on `contract_details_to_tracker.extractedContract
 * .totalInvestment` (the canonical post-2026-05 split source); falls back
 * to the legacy pre-split `trigger_and_extract.contractValue` for older
 * workflows that ran before the split. Returns undefined when missing.
 */
function readContractValue(context: Record<string, unknown>): number | undefined {
  const details = context['contract_details_to_tracker'] as
    | { extractedContract?: { totalInvestment?: number | null } | null }
    | undefined;
  const extracted = details?.extractedContract?.totalInvestment;
  if (typeof extracted === 'number') return extracted;
  const legacy = context['trigger_and_extract'] as { contractValue?: number } | undefined;
  return typeof legacy?.contractValue === 'number' ? legacy.contractValue : undefined;
}

/**
 * Reads the HubSpot deal name from the workflow context (step 0 writes it to
 * `trigger_event.dealProperties.dealname`). `displayName` already holds the
 * resolved company name, so this is the second, distinct identifier we surface
 * on the card. Returns undefined before step 0 runs or on non-sponsor types.
 */
function readDealName(context: Record<string, unknown>): string | undefined {
  const trigger = context['trigger_event'] as
    | { dealProperties?: { dealname?: string | null } | null }
    | undefined;
  const name = trigger?.dealProperties?.dealname;
  return typeof name === 'string' && name.trim() ? name.trim() : undefined;
}

/**
 * Derived status key → Dot status. Driven by the refined key (not the raw
 * workflow status) so a sponsor/external wait (`awaiting_reply`) gets the teal
 * `awaiting` dot while a human gate (`paused`) stays amber `waiting` — the two
 * waiting states must not blend. Dot has no 'paused'/'rejected' keys.
 */
function dotStatus(key: StatusKey): DotStatus {
  switch (key) {
    case 'running':
      return 'running';
    case 'paused':
      return 'waiting';
    case 'awaiting_reply':
      return 'awaiting';
    case 'rejected':
      return 'failed';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    default:
      // pending → cancelled-styled dot (greyed). The list rarely shows pending
      // since the engine flips to running within ~ms, but the fallback keeps
      // the card from crashing.
      return 'cancelled';
  }
}

/** Public props for WorkflowCard. */
export interface WorkflowCardProps {
  workflow: Workflow;
  /** Optional human-readable name of the current step (e.g., "Welcome email"). */
  currentStepName?: string;
  /** Called when the admin clicks Retry on a failed workflow. */
  onRetry?: (stepIndex: number) => void;
  /** Index of the failed step to retry (server-supplied via WorkflowList). */
  lastFailedStepIndex?: number;
  /** Truncated error message to render on the card (full message lives on Detail page). */
  lastErrorMessage?: string;
  /** Called when the admin clicks Delete (debug/testing). Confirms before invoking. */
  onDelete?: (workflowId: string) => void;
  /**
   * Called when the user toggles archive on this workflow. `archived` is the
   * desired next state (true → archive, false → restore). When provided, the
   * card renders an Archive (or Unarchive, if already archived) button.
   */
  onArchiveToggle?: (workflowId: string, archived: boolean) => void;
}

/**
 * Renders a single workflow card row. Click-to-navigate + Enter/Space
 * keyboard activation; Retry button uses stopPropagation so admin clicks
 * don't bubble into the card-level navigate.
 *
 * @sideEffects on click / Enter / Space, navigates to /workflows/:id
 * @idempotent yes — pure render of the workflow + meta props
 */
export function WorkflowCard({
  workflow: w,
  currentStepName,
  onRetry,
  lastFailedStepIndex,
  lastErrorMessage,
  onDelete,
  onArchiveToggle,
}: WorkflowCardProps) {
  const navigate = useNavigate();
  const totalSteps = getStepCount(w.type);
  const currentStep = Math.max(1, Math.min(totalSteps, w.currentStepIndex + 1));
  const contractValue = readContractValue(w.context);
  // displayName is the resolved company name (step 0 rewrites the placeholder).
  // The deal name is the larger primary identifier; show the company beneath it
  // only when it adds information (present and not identical to the deal name).
  const dealName = readDealName(w.context);
  const primaryName = dealName ?? w.displayName;
  const secondaryName =
    dealName && w.displayName && w.displayName !== dealName ? w.displayName : undefined;
  const typeKey: 'sponsor' | 'speaker' | 'test' =
    w.type === 'sponsor_onboarding'
      ? 'sponsor'
      : w.type === 'speaker_onboarding'
        ? 'speaker'
        : 'test';

  const railColor =
    typeKey === 'sponsor'
      ? 'bg-type-sponsor/40'
      : typeKey === 'speaker'
        ? 'bg-type-speaker/40'
        : 'bg-cancelled/40';

  // Refined status key drives the badge, dot, and progress so the sponsor-wait
  // (awaiting_reply) reads teal everywhere instead of blending into the amber
  // human-gate (paused) state.
  const statusKey = deriveWorkflowStatusKey(w);
  const isPulse = w.status === 'running' || w.status === 'paused';
  // Workflow has no `startedAt` column on the wire — engine flips status to
  // 'running' within ms of insert, so createdAt is effectively start time.
  // Use completedAt for completed/cancelled states so cards show the actual
  // finish time instead of the original submission.
  const relAnchor =
    (w.status === 'completed' || w.status === 'cancelled') && w.completedAt
      ? w.completedAt
      : w.createdAt;
  const relTime = formatDistanceToNow(new Date(relAnchor), { addSuffix: true });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(workflowPath(w))}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(workflowPath(w));
        }
      }}
      className={cn(
        'grid grid-cols-[4px_32px_1fr_auto] gap-3.5 items-center',
        'bg-bg border border-border rounded-lg pr-4 py-3.5 pl-0 mb-2.5',
        'cursor-pointer transition-colors duration-100',
        'hover:border-border-strong hover:bg-bg-soft',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
        // Archived rows only appear with "Show archived" on — dim them so it's
        // obvious they're offline and not part of the live queue.
        w.isArchived && 'opacity-55',
      )}
    >
      {/* 4px type-tinted rail */}
      <div className={cn('w-1 h-full self-stretch rounded-l-lg', railColor)} />

      {/* Status icon column */}
      <div className="flex items-center justify-center pl-2">
        <Dot status={dotStatus(statusKey)} pulse={isPulse} size={12} />
      </div>

      {/* Main column */}
      <div className="min-w-0 flex flex-col gap-1.5">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[15px] font-semibold truncate">{primaryName}</span>
          {w.isArchived && (
            <span className="text-[10px] font-medium uppercase tracking-wide text-text-subtle bg-bg-muted rounded px-1.5 py-0.5 shrink-0">
              Archived
            </span>
          )}
          {contractValue !== undefined && (
            <span className="text-[12px] text-text-muted tabular shrink-0">
              ${contractValue.toLocaleString()}
            </span>
          )}
        </div>
        {secondaryName ? (
          <div className="text-[12px] text-text-muted truncate -mt-0.5">{secondaryName}</div>
        ) : null}
        <div className="text-[12px] text-text-muted truncate">
          <span>
            Step {currentStep} of {totalSteps}
          </span>
          {currentStepName ? <span className="text-text-faint mx-1.5">·</span> : null}
          {currentStepName ? <span>{currentStepName}</span> : null}
        </div>
        {w.status === 'failed' && lastErrorMessage ? (
          <div
            className="font-mono text-[11px] text-failed bg-failed-soft border border-failed-border rounded px-2 py-1 truncate"
            title={lastErrorMessage}
          >
            {lastErrorMessage}
          </div>
        ) : null}
        <Progress total={totalSteps} current={currentStep} status={statusKey} className="mt-1" />
      </div>

      {/* Side meta */}
      <div className="flex flex-col gap-1.5 items-end shrink-0">
        <StatusBadge status={deriveWorkflowStatusKey(w)} />
        <span className="text-[11px] text-text-subtle tabular">{relTime}</span>
        {w.status === 'failed' && onRetry && lastFailedStepIndex !== undefined ? (
          <RoleGuard roles={['admin']}>
            <Button
              size="sm"
              variant="default"
              onClick={(e) => {
                e.stopPropagation();
                onRetry(lastFailedStepIndex);
              }}
            >
              <Icon name="retry" size={12} />
              Retry
            </Button>
          </RoleGuard>
        ) : null}
        {onArchiveToggle ? (
          <Button
            size="sm"
            variant="default"
            onClick={(e) => {
              e.stopPropagation();
              onArchiveToggle(w.id, !w.isArchived);
            }}
            title={w.isArchived ? 'Restore workflow to the list' : 'Hide workflow from the list'}
          >
            <Icon name={w.isArchived ? 'archive-restore' : 'archive'} size={12} />
            {w.isArchived ? 'Unarchive' : 'Archive'}
          </Button>
        ) : null}
        {onDelete ? (
          <RoleGuard roles={['admin']}>
            <Button
              size="sm"
              variant="default"
              onClick={(e) => {
                e.stopPropagation();
                const ok = window.confirm(
                  `Delete "${w.displayName}"? Removes the workflow and all its step executions, audit entries, and tracked emails. Cannot be undone.`,
                );
                if (ok) onDelete(w.id);
              }}
              title="Delete workflow"
            >
              <Icon name="x" size={12} />
              Delete
            </Button>
          </RoleGuard>
        ) : null}
      </div>
    </div>
  );
}
